import React from 'react';
import usePagination from '../../../hooks/usePagination';
import CommonPagination from '../../../components/common/CommonPagination';

export const PatientWorklistGrid = ({
  patients,
  loading,
  searchField,
  setSearchField,
  searchValue,
  setSearchValue,
  statusFilter,
  setStatusFilter,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  onRefresh,
  onSelectPatient,
  onOpenLog,
  selectedPatientId,
  summary,
}) => {
  const {
    page,
    pageSize,
    totalRecords,
    paginatedItems,
    setPage,
    setPageSize,
  } = usePagination({
    items: patients,
    resetDeps: [searchField, searchValue, statusFilter, fromDate, toDate],
  });
  return (
    <div className="path-worklist-view">
      {/* Workbench Single-Line Color-Coded KPI Strip */}
      <div className="path-kpi-strip">
        <button
          type="button"
          className={`path-kpi-chip chip-all ${statusFilter === 'ALL' ? 'active' : ''}`}
          onClick={() => setStatusFilter('ALL')}
          title="All Workload"
        >
          <span className="chip-icon">📊</span>
          <span className="chip-label">All</span>
          <span className="chip-count">{summary.totalEligibleToday || 0}</span>
        </button>

        <button
          type="button"
          className={`path-kpi-chip chip-collected ${statusFilter === 'COLLECTED' ? 'active' : ''}`}
          onClick={() => setStatusFilter('COLLECTED')}
          title="Sample Collected in Phlebotomy"
        >
          <span className="chip-icon">🧪</span>
          <span className="chip-label">Collected</span>
          <span className="chip-count">{summary.sampleCollectedCount || 0}</span>
        </button>

        <button
          type="button"
          className={`path-kpi-chip chip-received ${statusFilter === 'RECEIVED' ? 'active' : ''}`}
          onClick={() => setStatusFilter('RECEIVED')}
          title="Sample Received in Lab"
        >
          <span className="chip-icon">📥</span>
          <span className="chip-label">Received</span>
          <span className="chip-count">{summary.sampleReceivedCount || 0}</span>
        </button>

        <button
          type="button"
          className={`path-kpi-chip chip-pending ${statusFilter === 'PENDING' ? 'active' : ''}`}
          onClick={() => setStatusFilter('PENDING')}
          title="Pending Result Entry"
        >
          <span className="chip-icon">⏳</span>
          <span className="chip-label">Pending</span>
          <span className="chip-count">{summary.pendingCount || 0}</span>
        </button>

        <button
          type="button"
          className={`path-kpi-chip chip-draft ${statusFilter === 'DRAFT' ? 'active' : ''}`}
          onClick={() => setStatusFilter('DRAFT')}
          title="Draft / Result Entered (Unapproved)"
        >
          <span className="chip-icon">📝</span>
          <span className="chip-label">Draft</span>
          <span className="chip-count">{summary.draftCount || 0}</span>
        </button>

        <button
          type="button"
          className={`path-kpi-chip chip-approved ${statusFilter === 'APPROVED' ? 'active' : ''}`}
          onClick={() => setStatusFilter('APPROVED')}
          title="Approved & Signed Off"
        >
          <span className="chip-icon">✅</span>
          <span className="chip-label">Approved</span>
          <span className="chip-count">{summary.approvedTodayCount || 0}</span>
        </button>

        <button
          type="button"
          className={`path-kpi-chip chip-critical ${statusFilter === 'CRITICAL' ? 'active' : ''}`}
          onClick={() => setStatusFilter('CRITICAL')}
          title="Critical Panic Value Alerts"
        >
          <span className="chip-icon">⚠️</span>
          <span className="chip-label">Critical</span>
          <span className="chip-count">{summary.criticalTodayCount || 0}</span>
        </button>

        <button
          type="button"
          className={`path-kpi-chip chip-hold ${statusFilter === 'HOLD' ? 'active' : ''}`}
          onClick={() => setStatusFilter('HOLD')}
          title="On Hold Samples"
        >
          <span className="chip-icon">⏸️</span>
          <span className="chip-label">Hold</span>
          <span className="chip-count">{summary.holdCount || 0}</span>
        </button>
      </div>

      {/* Filter & Search Bar */}
      <div className="path-search-container">
        <div className="path-search-row">
          <div className="path-form-group">
            <label className="path-label">Search By</label>
            <select
              className="path-select"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
            >
              <option value="ALL">All Fields</option>
              <option value="UHID">UHID / Patient ID</option>
              <option value="LABNO">Lab / Reg No</option>
              <option value="PATIENT">Patient Name</option>
              <option value="PANEL">Panel / Client Name</option>
              <option value="BARCODE">Barcode No</option>
              <option value="SAMPLENO">Sample No</option>
              <option value="INVESTIGATION">Test / Investigation</option>
            </select>
          </div>

          <div className="path-form-group flex-grow">
            <label className="path-label">Search Query</label>
            <input
              type="text"
              className="path-input"
              placeholder="Search by UHID, Lab No, Patient Name, Panel, Barcode..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onRefresh()}
            />
          </div>

          <div className="path-form-group">
            <label className="path-label">Status</label>
            <select
              className="path-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="COLLECTED">🧪 Sample Collected</option>
              <option value="RECEIVED">📥 Sample Received</option>
              <option value="PENDING">⏳ Pending Entry</option>
              <option value="DRAFT">📝 Draft / Entered</option>
              <option value="APPROVED">✅ Approved</option>
              <option value="CRITICAL">⚠️ Critical Alert</option>
              <option value="HOLD">⏸️ On Hold</option>
            </select>
          </div>

          <div className="path-form-group">
            <label className="path-label">From Date</label>
            <input
              type="date"
              className="path-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="path-form-group">
            <label className="path-label">To Date</label>
            <input
              type="date"
              className="path-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <div className="path-form-actions">
            <button
              type="button"
              className="path-btn path-btn-primary"
              onClick={onRefresh}
              disabled={loading}
            >
              {loading ? 'Searching...' : '🔍 Search'}
            </button>
          </div>
        </div>
      </div>

      {/* Patient Master Grid */}
      <div className="path-patient-grid-card">
        <div className="path-card-header">
          <div className="path-card-title">
            <span>📋 Pathology Worklist Patients</span>
            <span className="path-count-badge">{patients.length} Patients Found</span>
          </div>
        </div>

        <div className="path-table-wrapper">
          <table className="path-table path-table-compact">
            <thead>
              <tr>
                <th style={{ width: '38px' }}>#</th>
                <th>Lab / Reg No</th>
                <th>UHID</th>
                <th>Patient Name</th>
                <th>Age / Gender</th>
                <th>Barcode / Accession</th>
                <th>Panel Name</th>
                <th>Department</th>
                <th style={{ textAlign: 'center' }}>Tests</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="path-table-empty">
                    <div className="path-spinner-text">⏳ Loading Pathology Worklist...</div>
                  </td>
                </tr>
              ) : totalRecords === 0 ? (
                <tr>
                  <td colSpan="11" className="path-table-empty">
                    <div className="path-empty-state">
                      <div className="path-empty-icon">📂</div>
                      <h4>No pathology samples found</h4>
                      <p>Try clearing filters or changing search criteria.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedItems.map((patient, index) => {
                  const sNo = (page - 1) * pageSize + index + 1;
                  const isSelected = selectedPatientId === patient.ledgerTransactionNo;
                  const hasCritical = patient.tests?.some((t) => t.hasCriticalResult);
                  const isHold = patient.tests?.some((t) => t.isHold === 1);
                  const isApproved = patient.tests?.every((t) => t.approved === 1);
                  const isDraft = patient.tests?.some((t) => t.resultFlag === 1 && t.approved === 0);

                  const allTestsOutsourced = patient.tests?.length > 0 && patient.tests.every((t) => t.isOutsourced);
                  const allTestsRejected = patient.tests?.length > 0 && patient.tests.every((t) => t.isRejected || t.isSampleCollected === 'R');

                  return (
                    <tr
                      key={patient.ledgerTransactionNo || index}
                      className={`path-patient-row ${isSelected ? 'selected' : ''}`}
                      onClick={() => onSelectPatient(patient)}
                    >
                      <td className="path-cell-bold">{sNo}</td>
                      <td>
                        <span className="path-cell-primary">{patient.ledgerTransactionNo}</span>
                      </td>
                      <td>
                        <span className="path-cell-subtle">{patient.patientId}</span>
                      </td>
                      <td>
                        <div className="path-patient-cell">
                          <span className="path-patient-name" title={patient.patientName}>
                            {patient.patientName}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="path-badge-demog">
                          {patient.ageGender || (patient.age ? `${patient.age}Y / ${patient.gender || '—'}` : (patient.gender ? `— / ${patient.gender}` : '— / —'))}
                        </span>
                      </td>
                      <td>
                        <span className="path-barcode-tag">{patient.barcodeNo || '—'}</span>
                      </td>
                      <td>
                        <span className="path-cell-panel" title={patient.panelName || 'Direct Walk-In'}>
                          <span className="path-panel-badge">
                            {patient.panelName || 'Direct Walk-In'}
                          </span>
                        </span>
                      </td>
                      <td>
                        <span className="path-badge-dept">{patient.departmentName || 'Pathology'}</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="path-test-count" title={patient.tests?.map((t) => t.testName).join(', ')}>
                          {patient.tests?.length || 1} Tests
                        </span>
                      </td>
                      <td>
                        <div className="path-status-pill-wrap">
                          {/* Single Authoritative Operational Status Badge */}
                          {hasCritical ? (
                            <span className="path-badge-result badge-result-critical" title="Contains Critical Alarm Value">
                              ⚠️ CRITICAL
                            </span>
                          ) : isHold ? (
                            <span className="path-badge-result badge-result-hold" title="Sample is on Hold">
                              ⏸️ On Hold
                            </span>
                          ) : isApproved ? (
                            <span className="path-badge-result badge-result-approved" title="Verified & Approved">
                              ✅ Approved
                            </span>
                          ) : isDraft ? (
                            <span className="path-badge-result badge-result-draft" title="Draft Result Entered">
                              📝 Draft
                            </span>
                          ) : allTestsRejected || patient.isRejected || patient.isSampleCollected === 'R' ? (
                            <span className="path-badge-result badge-result-rejected" title="Sample Rejected">
                              🚫 Rejected
                            </span>
                          ) : allTestsOutsourced || patient.isOutsourced ? (
                            <span className="path-badge-result badge-result-outsourced" title="Outsourced to Partner Lab">
                              🌐 Outsourced
                            </span>
                          ) : patient.sampleReceiveDate ? (
                            <span className="path-badge-sample badge-sample-received" title="Sample Received in Lab">
                              📥 Received
                            </span>
                          ) : patient.sampleCollectionDate || patient.isSampleCollected === 'Y' || patient.isSampleCollected === 'S' ? (
                            <span className="path-badge-sample badge-sample-collected" title="Sample Collected in Phlebotomy">
                              🧪 Collected
                            </span>
                          ) : (
                            <span className="path-badge-result badge-result-pending" title="Pending Result Entry">
                              ⏳ Pending
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="path-action-cell-btns">
                          <button
                            type="button"
                            className="path-btn path-btn-sm path-btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectPatient(patient);
                            }}
                            title="Enter Pathology Results"
                          >
                            Enter Results ➔
                          </button>
                          <button
                            type="button"
                            className="path-btn path-btn-sm path-btn-log"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onOpenLog) onOpenLog(patient);
                            }}
                            title="View Patient Visit & Sample Lifecycle Log"
                          >
                            📜 Log
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Centralized Pagination */}
        <CommonPagination
          page={page}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default PatientWorklistGrid;
